import React, { useCallback } from "react";
import { useSelector, RootStateOrAny, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Pivot, PivotItem } from "office-ui-fabric-react/lib/Pivot";

import ClientBasicInfo from "./displayData/ClientBasicInfo";
import ReceptionPlacesForm from "./forms/PickUpPointsForm";
import UsersForm from "./forms/UsersForm";
import DepartmentsForm from "./forms/DepartmentsForm";
import CompanyDetailData from "./CompanyDetailData";
import {
  departmentsColumns,
  generateDepartments,
} from "../colums/departments-columns";
import {  generateUsers } from "../colums/users-columns";
import {
  pickUpPointsColumns,
  generatePickUpPoints,
  pickUpPointsColumnsShort,
} from "../colums/pickUpPoints-columns";
import {
  getDepartments,
  getUsers,
  getReceptionPlaces,
  getCompaniesAttachments,
} from "../../../../connectors/clients/connectors/index";
import {
  setActiveDepartment,
  setActivePickUpPoint,
  setActiveUser,
  clearData,
  setActiveTab,
  setActiveFiles,
} from "../../../../config/redux/clients/panel/clientPanelActions";
import { clearAlerts } from "../../../../config/redux/alerts/alertsActions";
import BasicInfoForm from "./forms/BasicInfoForm";
import { DirectionalHint, FontIcon, TooltipDelay, TooltipHost } from "office-ui-fabric-react";
import FilesUploadForm from "./forms/FilesUploadForm";
import { filesColumns, generateFilesList } from "../colums/files-columns";

export enum TabsNames {
  BASIC_INFO = "Informacje podstawowe",
  PICKUP_POINTS = "Punkty odbioru",
  USERS = "Użytkownicy",
  DEPARTMENTS = "Oddziały",
  FILES = "Pliki"
}

const ClientTabs: React.FC = () => {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();
  const { editing, reload, basicInfo } = useSelector(
    (state: RootStateOrAny) => state.clientPanel
  );

  const handleClick = useCallback((e: any) => {
    dispatch(setActiveTab(e.props.id));
    dispatch(clearAlerts());
    dispatch(clearData());
  }, []);

  const usersColumns = [
    {
      key: "column1",
      name: t("nav.colEmail") as string,
      fieldName: "email",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
      onRender: (item: any) => ( item.isInvitationInProgress ?
        <><TooltipHost
        content={t("nav.invitationSent") as string}
        delay={TooltipDelay.zero}
        directionalHint={DirectionalHint.bottomCenter}
      ><FontIcon iconName='mail'/> </TooltipHost>{item.email}</> : item.email
      ),
    },
    {
      key: "column2",
      name: t("nav.colFirstName") as string,
      fieldName: "firstNamee",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "column3",
      name: t("nav.colLastName") as string,
      fieldName: "lastName",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "column4",
      name: t("nav.colPhone") as string,
      fieldName: "phone",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "column5",
      name: t("nav.colDepartment") as string,
      fieldName: "departmentName",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
    },
  ];

  const usersColumnsShort = [
    {
      key: "column1",
      name: t("nav.colEmail") as string,
      fieldName: "email",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
      onRender: (item: any) => ( item.isInvitationInProgress ?
        <><TooltipHost
        content={t("nav.invitationSent") as string}
        delay={TooltipDelay.zero}
        directionalHint={DirectionalHint.bottomCenter}
      ><FontIcon iconName='mail'/> </TooltipHost>{item.email}</> : item.email
      ),
    },
    {
      key: "column2",
      name: t("nav.colFirstName") as string,
      fieldName: "firstName",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "column3",
      name: t("nav.colLastName") as string,
      fieldName: "lastName",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "column4",
      name: t("nav.colPhone") as string,
      fieldName: "phone",
      minWidth: 50,
      maxWidth: 100,
      isResizable: true,
    },
  ];

  return (
    <Pivot onLinkClick={handleClick}>
      <PivotItem headerText={t("nav.tabBasicInfo") as string} id={TabsNames.BASIC_INFO}>
        {basicInfo && (editing ? <BasicInfoForm /> : <ClientBasicInfo />)}
      </PivotItem>
      {}
      <PivotItem
        headerText={t("nav.tabPickupPoints") as string}
        id={TabsNames.PICKUP_POINTS}
      >
        {editing ? (
          <ReceptionPlacesForm />
        ) : (
          <CompanyDetailData
            columns={
              basicInfo.hasOwnProperty("basicInformations") &&
              basicInfo.basicInformations.multiDepartment
                ? pickUpPointsColumns(t)
                : pickUpPointsColumnsShort(t)
            }
            generateItems={generatePickUpPoints}
            getData={getReceptionPlaces}
            setActiveData={setActivePickUpPoint}
            change={reload}
          />
        )}
      </PivotItem>
      <PivotItem headerText={t("nav.tabUsers") as string} id={TabsNames.USERS}>
        {editing ? (
          <UsersForm />
        ) : (
          <CompanyDetailData
            columns={basicInfo.basicInformations && basicInfo.basicInformations.multiDepartment ? usersColumns : usersColumnsShort}
            generateItems={generateUsers}
            getData={getUsers}
            setActiveData={setActiveUser}
            change={reload}
          />
        )}
      </PivotItem>
      {basicInfo.hasOwnProperty("basicInformations") &&
        basicInfo.basicInformations.multiDepartment && (
          <PivotItem
            headerText={t("nav.tabDepartments") as string}
            id={TabsNames.DEPARTMENTS}
          >
            {editing ? (
              <DepartmentsForm />
            ) : (
              <CompanyDetailData
                columns={departmentsColumns(t)}
                generateItems={generateDepartments}
                getData={getDepartments}
                setActiveData={setActiveDepartment}
                change={reload}
              />
            )}
          </PivotItem>
        )}
         <PivotItem
          headerText={t("nav.tabFiles") as string}
          id={TabsNames.FILES}>
                    {editing ? (
                        <FilesUploadForm />
        ) : (
          <CompanyDetailData
            columns={filesColumns(t)
            }
            generateItems={generateFilesList}
            getData={getCompaniesAttachments}
            setActiveData={setActiveFiles}
            change={reload}
          />)}

         </PivotItem>
    </Pivot>
  );
};

export default ClientTabs;
